import {
  Component, OnInit, OnDestroy, ViewChild, NgZone,
  ChangeDetectorRef, ChangeDetectionStrategy,
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Observable ,  Subscription, merge } from 'rxjs';

import * as _ from 'lodash';

import { receiversPagesData, RECEIVERS_PAGE_MY_NAME, RECEIVERS_PAGE_LIB_NAME } from 'utils/receivers-pages-data';
import { showAnimation } from 'shared/search-page/animations';
import { SearchModel } from 'shared/search/search.model';
import { AuthService } from 'shared/auth/auth.service';
import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { ReceiversSearchService } from 'shared/receivers/search/receivers-search.service';
import { AppModalService } from 'shared/modal/modal.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { ReceiversService } from 'shared/receivers/receivers.service';
import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { DynamicNavsComponent } from 'shared/nav/dynamic-navs/dynamic-navs.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';
import { columns } from 'shared/receivers/search/search-results-columns';
import { APPROVED, REJECTED } from 'shared/receivers/receivers-statuses-id';
import * as receiverActions from 'shared/receivers/receivers-actions';
import { setUnreadCommentsCount } from 'shared/comments/comments-helpers';
import { CommentsService } from 'shared/comments/comments.service';
import { ScorecardService } from 'shared/scorecard/scorecard.service';

import { AppRoutingService } from '../app-routing.service';
import { rightPanelNavs } from './right-panel-navs';
import { ReceiversHeaderComponent } from './components/receivers-header/receivers-header.component';
import { headerActions } from './receivers-actions';
import {filter, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'sd-receivers',
  templateUrl: 'receivers.component.html',
  styleUrls: ['receivers.component.scss'],
  animations: [showAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiversComponent extends SearchPageComponent implements OnInit, OnDestroy {
  @ViewChild(ReceiversHeaderComponent, {static: false})
  declare headerEl: ReceiversHeaderComponent | void;

  @ViewChild(DynamicNavsComponent, {static: false})
  dynamicNavs: DynamicNavsComponent | void;

  @ViewChild(SimpleConfirmModalComponent, {static: false})
  declare modal: SimpleConfirmModalComponent | void;

  actions: any = headerActions; // actions in header
  receiverActions = receiverActions; // action in action-panel
  pagesData: any = receiversPagesData;
  pagesMenuData: any = {};
  contentSelector: string = '#receivers-content';
  ghostValue: string = 'receivers';
  maxColumnsInSearch: number = 10;
  rowsHover = true;
  selectedReceiver: any;

  timezone: any;
  saveSuffix: string;
  showSidedrawer: boolean = true; // use this field to show sidedrawer

  // right panel
  rightPanelMovedOut: boolean = true; // indicate right panel toggle
  rightPanelNavs: Array<any> = rightPanelNavs;

  isActive: boolean = true;
  warningModalTitle: string;
  warningModalDescription: string;

  private routerChangeSub: Subscription;

  statusColorMap: any = {
    '1': 'selectiveYellow', // requested
    '2': 'green', // accepted
    '3': 'red', // rejected
    '100': 'transparent', // no-status
  };

  constructor(
    public searchService: ReceiversSearchService,
    protected router: Router,
    protected brandService: BrandService,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected route: ActivatedRoute,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
    private authService: AuthService,
    private appModalService: AppModalService,
    private receiversService: ReceiversService,
    private commentsService: CommentsService,
    private scorecardService: ScorecardService,
  ) {
    super(
      searchService, brandService, pageService, processService, scrollToService,
      appRoutingService, null, permissionsService, ngZone, ref, route, location,
      savedSearchesService, router, userService,
    );
    this.searchResultsColumns = columns;
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  get rootUrl(): string {
    // my-products, import-manager, export-manager
    return this.route.snapshot.url[0].path;
  }

  get page(): string {
    return this.route.snapshot.params['page'];
  }

  get isLeftTabActive(): boolean {
    return this.page === 'my';
  }

  get isRightTabActive(): boolean {
    return this.page === 'library';
  }

  get currentRoute(): string {
    return this.searchModel && this.searchModel.route;
  }

  get headerColor(): string {
    return 'cornflowerBlue';
  }

  get selectedRowsIds(): Array<any> {
    return _.map(this.selectedRows, 'id');
  }

  get localStorageName(): string {
    if (_.has(this, 'user.id')) {
      return `receiversSearchModel.${this.brandService.getBrand()}.${this.user.id}`;
    }
    return null;
  }

  // implemented
  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(
        localStorage.getItem(`receiversSearchModel.${this.saveSuffix}`)
      );
    } else {
      return null;
    }
  }

  // implemented
  get searchValue() {
    if (!this.searchModel) {
      return;
    }
    if (this.searchModel.searchValue && this.searchModel.searchValue.length) {
      return this.searchModel.searchValue;
    }
    return null;
  }

  get isAdmin(): boolean {
    return this.permissionsService.hasPermission(['admin']);
  }

  get leftTabTitle(): string {
    return this.pagesData[RECEIVERS_PAGE_MY_NAME].title;
  }

  get rightTabTitle(): string {
    return this.pagesData[RECEIVERS_PAGE_LIB_NAME].title;
  }

  get allowApproveReceivers(): boolean {
    return this.page === 'library';
  }

  get allowRemoveReceivers(): boolean {
    return this.page === 'my';
  }

  get activeTabComponent(): any {
    return _.get(this.dynamicNavs, 'componentRef.instance');
  }

  get activeTabName(): any {
    return _.get(this.dynamicNavs, 'activeNav.name');
  }

  get statusesFiltered(): any {
    return _.filter(this.statuses, (status: any) => {
      return status.id !== 200;
    });
  }

  get rowStatusHidden(): boolean {
    return this.currentRoute === 'my';
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.timezone = _.get(this.route, 'snapshot.data.preferences.timezone');
    this.handleRouterChange();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.isActive = false;
    setUnreadCommentsCount(this.rightPanelNavs, 0);
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  initPage() {
    if (!this.searchModel) {
      return;
    }
    this.searchModel.route = this.page;
  }

  updateSaveSuffix() {
    // {:page}.{:searchId?}.{:brandId}.{:userId}
    if (_.has(this, 'user.id')) {
      this.saveSuffix = this.page;
      const searchId = this.route.snapshot.params['searchId'];
      if (searchId) {
        this.saveSuffix = `${this.saveSuffix}.${searchId}`;
      }
      this.saveSuffix = `${this.saveSuffix}.${this.brandId}.${this.user.id}`;
    }
  }

  initSubscriptions() {
    super.initSubscriptions();
    this.routerChangeSub = this.subscribeRouterChange();
  }

  destroySubscriptions() {
    super.destroySubscriptions();
    if (this.routerChangeSub) {
      this.routerChangeSub.unsubscribe();
    }
  }

  // implemented
  storeSearchModel(searchModel: SearchModel) {
    if (_.has(this, 'user.id')) {
       localStorage.setItem(
        `receiversSearchModel.${this.saveSuffix}`,
        JSON.stringify(searchModel)
      );
    }
  }

  removeSearchModel() {
    if (_.has(this, 'user.id')) {
      localStorage.removeItem(`receiversSearchModel.${this.saveSuffix}`);
    }
  }


  setPagesMenuItemVisibility(menuName: string, visibility: boolean) {
    const menuItem: any = _.find(this.pagesMenuData, {name: menuName});
    if (menuItem) {
      menuItem.visible = visibility;
    }
  }

  runSearch(searchModel: SearchModel) {
    if (!this.searchModel) {
      return;
    }
    this.rightPanelMovedOut = true;
    // request only my approved receivers
    if (searchModel.route === 'my') {
      _.set(this.searchModel, 'statusModel.id', 2);
    }
    this.startLoading();
    searchModel.searchId = this.getSearchId();
    this.searchService.runSearch(this.brandId, searchModel)
    .subscribe(searchResults => {
      if (_.has(this.searchModel, 'statusModel.id')) {
          // @ts-ignore
        this.searchModel.statusModel = _.find(this.statuses, {id: this.searchModel.statusModel.id});
      }
    }, null, () => this.stopLoading());
  }

  applyAdvancedSearchFilters(): void {
    if (!this.searchModel) {
      return;
    }
    const tagsFilters = this.advancedSearch.selectedTagsFilters;
    const stateFilters = this.advancedSearch.selectedStateFilters;

    // replace all tags & state filters
    let filters = _.filter(this.searchModel.filters, (_filter: any) => {
      return _filter._id !== 'tags' && _filter._id !== 'state';
    });
    if (tagsFilters) {
      filters = _.concat(filters, tagsFilters);
    }
    if (stateFilters) {
      filters = _.concat(filters, stateFilters);
    }
    this.searchModel.filters = filters;
  }

  /*******************************************
   * COMPONENT EVENT HANDLERS
   *******************************************/

  onAdvancedSearchRun() {
    if (this.searchModel) {
      this.searchModel.currentPage = 1;
    }
    super.onAdvancedSearchRun();
  }

  // redefine parent methods
  onActionSelected(action: any) {
    this.warningModalTitle = `${(action === receiverActions.REJECT_ACTION) ? 'Reject' : 'Approve'} selected receivers?`;
    if (this.searchModel && this.searchModel.route === 'my') {
      this.warningModalDescription = `
        This will move selected receivers back to&nbsp;your library.
        To&nbsp;find them again, search your library by&nbsp;status to&nbsp;see all rejected receivers.`;
    } else {
      this.warningModalDescription = (action === receiverActions.REJECT_ACTION) ?
        `Selected receivers will be&nbsp;marked as&nbsp;rejected.
        Find them again by&nbsp;searching for rejected receivers.` :

        `This will add selected receivers to&nbsp;your library.
        Not all receivers may be&nbsp;ready to&nbsp;accept one-click exports.`;
    }
    this.actionData = action;
    if (this.modal instanceof SimpleConfirmModalComponent) {
      this.modal.show();
    }
  }

  onLeftTabClick() {
    this.router.navigate(['/receivers/my']);
  }

  onRightTabClick() {
    this.router.navigate(['/receivers/library']);
  }

  onCellClick(event: any) {
    const {row, column} = event;
    this.selectedReceiver = row;
    if (column.col === 'name') {
      this.rightPanelMovedOut = false;
      if (this.dynamicNavs instanceof DynamicNavsComponent) {
        this.dynamicNavs.selectNav('details');
      }
      this.activeTabComponent.setData(this.selectedReceiver);
      this.activeTabComponent.receiverChanged$
        .pipe(takeWhile(() => this.isActive))
        .subscribe((data: any) => {
          if (this.searchModel) {
            this.runSearch(this.searchModel);
          }
          this.ref.markForCheck();
        });
      this.setUnreadCommentsCount();
    }
  }

  onRightPanelNavChanged(event: any) {
    if (!this.selectedReceiver) {
      return;
    }
    const tabName = _.get(event, 'nav.name');
    switch (tabName) {
      case 'details':
        this.activeTabComponent.setData(this.selectedReceiver);
        break;
      case 'logs':
        this.activeTabComponent.setData(this.selectedReceiver, this.timezone);
        break;
      case 'comments':
        this.activeTabComponent.fetch(this.brandId, this.selectedReceiver._id, 'receivers');

        // listen to events which change unread count
        merge(
          this.activeTabComponent.commentMarkedAsRead,
          this.activeTabComponent.commentResolved
        )
          .pipe(takeWhile(() => this.activeTabComponent.isActive))
          .subscribe(() => {
            this.setUnreadCommentsCount();
          });
        break;
      default:
        break;
    }
  }

  onConfirmWarningModal() {
    if (this.modal instanceof SimpleConfirmModalComponent) {
      this.modal.hide();
    }
    this.startLoading();

    const ids = !this.allSelected ? _.map(this.searchService.selectedItems, (selectedItem: any) => selectedItem._id) : [];
    let _filter;

    if (this.allSelected) {
        _filter = {
        search: this.searchModel && this.searchModel.searchValue || '',
        status: (this.searchModel && this.searchModel.statusModel) ? this.searchModel.statusModel.id.toString() : '',
      };
    }

    this.receiversService.changeStatuses(
      this.brandId,
      ids,
        _filter,
      this.actionData === receiverActions.REJECT_ACTION ? REJECTED : APPROVED
    )
      .subscribe((res: any) => {
        this.searchService.deselectAll();
        if (this.searchModel) {
          this.runSearch(this.searchModel);
        }
        // hard refresh current scorecards list
        // because it could be receiver with a scorecard
        this.scorecardService.getScorecards(this.brandId, true).subscribe();
        this.ref.markForCheck();
      });
  }

  onCancelWarningModal() {
    if (this.modal instanceof SimpleConfirmModalComponent) {
      this.modal.hide();
    }
  }

  onPageActionSelect(actionData: any) {
    if (actionData.action === 'add-receiver') {
      window.open('https://partshub.com/receivercontactform', '_blank');
    }
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

   protected addDataToColumns(): void {
     const columnsWithData: Array<any> = [];
     _.each(this.searchResultsColumns, (column: any) => {
        const newColumn = _.cloneDeep(column);
        const foundCol: any = _.find(columns, {'col': column.col} );
        if (foundCol && foundCol.data) {
          newColumn.data = foundCol.data;
        }
        columnsWithData.push(newColumn);
     });
     this.searchResultsColumns = columnsWithData;
   }

   protected toggleRightPanel() {
     this.rightPanelMovedOut = !this.rightPanelMovedOut;
     this.ref.markForCheck();
   }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private subscribeRouterChange(): Subscription {
    return this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleRouterChange(event);
      });
  }

  private handleRouterChange(event?: NavigationEnd): void {
    this.updateSaveSuffix();
    this.rightPanelMovedOut = true;
    this.showEmptyResultsState = false;
    const route = this.page;

    if (this.searchModel && this.searchModel.route !== route ) {
      this.searchService.searchResults = [];
      this.searchService.selectAllChecked = [];
      this.searchService.selectedItems = [];
      this.searchService.allSelected = false;

      this.toggleAdvancedSearch(false);
    }

    this.userService.getUser()
      .subscribe( (user) => {
        this.user = user;
        this.updateSaveSuffix();
        if (this.storedSearchModel && !_.get(this.route.snapshot.queryParams, 'csm')) {
          this.searchModel = this.storedSearchModel;
        } else {
          this.searchModel = new SearchModel();
          this.searchModel.route = route;
        }
        this.resetSearchValue();
        this.handleSearchModelBeforeSearchSubj
          .pipe(takeWhile(() => this.isPageActive))
          .subscribe(() => {
            if (this.searchModel) {
              this._searchModel.next(this.searchModel);
            }
          });
        this.handleSearchModelBeforeSearch();
      });

    // set actions visibility
    this.setRouteActions(route);
  }

  private  getSearchId(): any {
    const routeSearchId = this.route.snapshot.params['searchId'];
    return routeSearchId ? routeSearchId : null;
  }

  private setRouteActions(route: string) {
    this.actions = (route === 'my') ? [] : _.cloneDeep(headerActions);

    _.each(this.actions, (action: any) => {
      action.hidden = !_.includes(action.availableOn, route);
    });
  }

  private setUnreadCommentsCount() {
    this.commentsService.getUnreadCount(this.brandId, 'receivers', this.selectedReceiver._id)
      .pipe(takeWhile(() => this.isPageActive))
      .subscribe((data: any) => {
        setUnreadCommentsCount(this.rightPanelNavs, data.count);
        this.ref.markForCheck();
      });
  }

  // inherited method of search-page component
  handleSearchModelBeforeSearch() {
    this.handleSearchModelBeforeSearchSubj.next(true);
  }
}
